// 地图点选仅产生现有场景模型的坐标，不改变地图单元、评分或保存格式。
export function sceneGeometry (kind, points) {
  const valid = point => point && Number.isFinite(point.x) && Number.isFinite(point.y)
  if (!points.length || !points.every(valid)) return null
  const a = points[0], b = points[1]
  if (kind === 'landmarks') return { x: a.x, y: a.y }
  if (!b) return null
  if (kind === 'roads' || kind === 'bridges') return a.x === b.x && a.y === b.y ? null : { x1: a.x, y1: a.y, x2: b.x, y2: b.y }
  if (kind === 'waterBodies') {
    const width = Number(Math.abs(a.x - b.x).toFixed(2)), height = Number(Math.abs(a.y - b.y).toFixed(2))
    return width && height ? { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width, height } : null
  }
  return null
}

// 只识别当前场景类型；距离容差由画布缩放换算，避免细路段难以点中。
export function sceneHitIndex (kind, items, point, tolerance = 6) {
  const finite = value => value !== '' && value != null && Number.isFinite(Number(value))
  if (!point || !finite(point.x) || !finite(point.y) || !Array.isArray(items)) return -1
  const x = Number(point.x), y = Number(point.y), padding = Math.max(0, Number(tolerance) || 0)
  let best = -1, bestDistance = Infinity
  items.forEach((item, index) => {
    if (!item) return
    let distance = Infinity
    if (kind === 'roads' || kind === 'bridges') {
      if (!['x1','y1','x2','y2','w'].every(key => finite(item[key])) || Number(item.w) <= 0) return
      const ax = Number(item.x1), ay = Number(item.y1), dx = Number(item.x2) - ax, dy = Number(item.y2) - ay, square = dx * dx + dy * dy
      if (!square) return
      const ratio = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / square))
      const gap = Math.hypot(x - ax - ratio * dx, y - ay - ratio * dy)
      if (gap <= Number(item.w) / 2 + padding) distance = gap
    } else if (kind === 'waterBodies') {
      if (!['x','y','width','height'].every(key => finite(item[key])) || Number(item.width) <= 0 || Number(item.height) <= 0) return
      const left = Number(item.x), top = Number(item.y), width = Number(item.width), height = Number(item.height)
      if (item.type === 'POND') {
        if (((x-left-width/2)/(width/2+padding))**2 + ((y-top-height/2)/(height/2+padding))**2 <= 1) distance = 0
      } else if (x >= left-padding && x <= left+width+padding && y >= top-padding && y <= top+height+padding) distance = 0
    } else if (kind === 'landmarks' && finite(item.x) && finite(item.y)) {
      const gap = Math.hypot(x - Number(item.x), y - Number(item.y))
      if (gap <= 9.5 + padding) distance = gap
    }
    if (Number.isFinite(distance) && distance <= bestDistance) { best = index; bestDistance = distance }
  })
  return best
}

// 拖动单个端点或水体角点，固定另一端；仅返回坐标，不回写表单或附带样式。
export function sceneHandleGeometry (kind, original, handle, point) {
  const finite = value => (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && Number.isFinite(Number(value))
  if (!original || !point || !['a', 'b'].includes(handle) || !finite(point.x) || !finite(point.y)) return null
  const target = { x: Number(point.x), y: Number(point.y) }
  if (kind === 'roads' || kind === 'bridges') {
    if (!['x1', 'y1', 'x2', 'y2'].every(key => finite(original[key]))) return null
    const a = { x: Number(original.x1), y: Number(original.y1) }, b = { x: Number(original.x2), y: Number(original.y2) }
    if (a.x === b.x && a.y === b.y) return null
    return sceneGeometry(kind, handle === 'a' ? [target, b] : [a, target])
  }
  if (kind === 'waterBodies') {
    if (!['x', 'y', 'width', 'height'].every(key => finite(original[key])) || Number(original.width) <= 0 || Number(original.height) <= 0) return null
    const left = Number(original.x), top = Number(original.y), right = left + Number(original.width), bottom = top + Number(original.height)
    if (!Number.isFinite(right) || !Number.isFinite(bottom) || left === right || top === bottom) return null
    const fixed = handle === 'a' ? { x: right, y: bottom } : { x: left, y: top }
    const width = Math.abs(target.x - fixed.x), height = Math.abs(target.y - fixed.y)
    if (!Number.isFinite(width) || !Number.isFinite(height) || !width || !height) return null
    return { x: Math.min(target.x, fixed.x), y: Math.min(target.y, fixed.y), width, height }
  }
  return null
}

// 按可见场景的类型优先级点选，桥面先于重叠道路；item 保持模板中的原引用（含表单预览）。
export function sceneObjectHit (template, point, layers, tolerance = 6, automaticBridges = []) {
  if (!template || !layers) return null
  const candidates = [
    ['landmarks', template.landmarks, layers.LANDMARKS, false],
    ['bridges', template.bridges, layers.ROAD, false],
    ['bridges', automaticBridges, layers.ROAD && layers.WATER, true],
    ['roads', template.roads, layers.ROAD, false],
    ['waterBodies', template.waterBodies, layers.WATER, false]
  ]
  for (const [kind, items, visible, automatic] of candidates) {
    if (!visible) continue
    const index = sceneHitIndex(kind, items, point, tolerance)
    if (index >= 0) return { kind, index, item: items[index], automatic }
  }
  return null
}
